<template>
  <div id="map-controls-sidebar-select-vessel">
    <vs-sidebar
      click-not-close
      hidden-background
      position-right
      v-model="activeSelectVesselSidebar"
      class="items-no-padding"
    >
      <div class="h-full">
        <div
          class="customizer-header mt-6 mb-6 flex items-center justify-between px-6"
        >
          <div>
            <span class="aisea-text-18-700-white">選択中</span>
          </div>
          <feather-icon
            icon="XIcon"
            @click.stop="SET_ACTIVE_SELECT_VESSEL_SIDEBAR(false)"
            svgClasses="live-map-controls-close"
            class="cursor-pointer"
          ></feather-icon>
        </div>

        <div class="bookmark-area">
          <div
            v-for="(favorite, index) in favoriteList"
            :key = "index"
            class="bookmark-button"
            :class="{selected: selectedFavoriteIndex == index}"
            @click="selectedFavoriteIndex = index"
            >
            <input
              v-model="favoriteLabelList[index]"
              maxlength = "9"
              :disabled="selectedFavoriteIndex != index"
              @blur="changeFavoriteLabel({index: index, label: favoriteLabelList[index]})"
              @keydown.enter="fixFavoriteLabel" 
              @compositionstart="isFavoriteLabelComposing=true"
              @compositionend="isFavoriteLabelComposing=false"
            >
          </div>
        </div>

        <div class="search-control-area">
          <div class="search-area">
            <input type="text" v-model="searchKeyword" placeholder="船舶名を検索" maxlength = "30">
            <feather-icon class="search-icon" icon="SearchIcon" svgClasses="h-5 w-5" />
          </div>

          <div class="control-area">
            <vx-tooltip text="新規グループを追加する" position="bottom" color="#2D9CDB">
              <div class="new-group-button control-button" @click="switchGroupAddOpen()">
                <feather-icon class="folder-plus-icon control-icon" icon="FolderPlusIcon" svgClasses="h-5 w-5" />
              </div>
            </vx-tooltip>
            <vx-tooltip text="表示順序を変更する" position="left" color="#2D9CDB">
              <div class="list-button control-button" :class="{active: isEditOrder}" @click="switchEditOrder">
                <feather-icon class="list-icon control-icon" icon="ListIcon" svgClasses="h-5 w-5" />
              </div>
            </vx-tooltip>
            <!-- TODO: 仕様調整中 手動の並べ替えとかぶるので一旦コメントアウト
            <div class="sort-switch">
              <img src="@/assets/images/icons/lolcal/icon_sort.svg" class="sort-icon" />
              <div class="sort-button" :class="{selected: sortMode=='A-Z'}" @click="sortMode='A-Z'">A-Z</div>
              <div class="sort-button" :class="{selected: sortMode=='登録順'}" @click="sortMode='登録順'">登録順</div>
            </div>
            -->
          </div>
        </div>

        <div class="scrollable-area">
          <transition name="smooth">
            <group-edit-panel
              v-show="isGroupAddOpen"
              @closeGroupEdit="switchGroupAddOpen()"
              @clickSaveButton="callGroupAddSave"
              mode="add">
            </group-edit-panel>
          </transition>
          <draggable
            v-model="displayFavoriteList[selectedFavoriteIndex].groupList"
            @start="drag=true"
            @end="drag=false"
            @sort="callUpdateGroupOrder(selectedFavoriteIndex)"
            v-bind="dragOptions"
          >
            <div class="group-container" v-for="(group, index) in displayFavoriteList[selectedFavoriteIndex].groupList" :key="index">
              <div class="group-bar"
                :class="{selected: isTargetGroupContentOpened(group.documentId), first: index == 0}"
                :style="'--color:' + group.color + ';' + '--hover:' + getHoverColor(group.color) + ';'"
                @click="switchGroupContent(group.documentId)"
              >
                <span class="group-name" :style="{'--color': group.color}">{{group.label}}</span>
                <span class="content-length" :style="'background:' + group.color + ';'">{{group.vesselList.length}}</span>
                <feather-icon
                  class="more-icon"
                  icon="MoreHorizontalIcon"
                  svgClasses="h-5 w-5"
                  :style="'color:' + group.color + ';'"
                  @click.stop="switchGroupEdit(group.documentId)"
                />
                <div class="group-bar-background"></div>
              </div>

              <transition name="smooth">
                <group-edit-panel
                  v-show="isTargetGroupEditOpened(group.documentId)"
                  @closeGroupEdit="switchGroupEdit(group.documentId)"
                  @clickSaveButton="callGroupEditSave"
                  @clickDeleteButton="callGroupEditDelete"
                  :isShow="isTargetGroupEditOpened(group.documentId)"
                  :documentId="group.documentId"
                  :initialGroupName="group.label"
                  :initialColor="group.color"
                  mode="edit">
                </group-edit-panel>
              </transition>
              <transition name="smooth">
                <ul v-if="isTargetGroupContentOpened(group.documentId)">
                  <li
                    class="pb-2 pl-4 pr-4"
                    v-for="vesselId in group.vesselList"
                    :key="vesselId"
                  >
                    <selected-vessel-card
                      :selectedVessel="getMonitoringVesselById(vesselId)"
                      @showDetails="showVesselDetail($event)"
                      @jumpToMap="jumpToMap($event)"
                      @onFetchWake="onFetchWake($event)"
                      @remove="callDeleteVesselFromGroup(vesselId, selectedFavoriteIndex, group.documentId)"
                      @onChangePosition="setSelectedVesselPosition($event)"
                      @createExpiredPopup="createExpiredPopup($event)"
                    ></selected-vessel-card>
                  </li>
                </ul>
              </transition>
            </div>
          </draggable>
        </div>
      </div>
    </vs-sidebar>
  </div>
</template>

<script>
import SelectedVesselCard from "./SelectedVesselCard";
import GroupEditPanel from "./GroupEditPanel.vue";
import draggable from 'vuedraggable'

import {createNamespacedHelpers} from "vuex";
import { GROUP_COLOR_PALETTE } from "@/config/const.js"
import _ from "lodash";

const {
  mapActions: mapActionsOfLiveMap,
  mapState: mapStateOfLiveMap,
  mapGetters: mapGettersOfLiveMap,
  mapMutations: mapMutationsOfLiveMap
} = createNamespacedHelpers("liveMap");

export default {
  components: {
    SelectedVesselCard,
    GroupEditPanel,
    draggable,
  },
  props: {
    map: Object
  },
  data() {
    return {
      drag: false,
      settings: {
        suppressScrollX: true,
        maxScrollbarLength: 60,
        wheelSpeed: 0.6
      },
      expiredPopup: null,
      timerId: null,

      selectedFavoriteIndex: 0,

      groupSubscribeList: [],

      sortMode: "A-Z",
      isEditOrder: false,

      searchKeyword: "",

      favoriteLabelList: ["", "", ""],
      isFavoriteLabelComposing: false,

      isGroupAddOpen: false,
      openedEditGroups: [],
      openedContentGroups: [],
      
      editGroupName: "",

      deletePopupActive: false,
    };
  },
  computed: {
    ...mapStateOfLiveMap({
      monitoringVessels: state => state.monitoringVessels,
      activeSelectVesselSidebar: state => state.activeSelectVesselSidebar,
      favoriteList: state => state.favoriteList
    }),
    ...mapGettersOfLiveMap(["hasWakes", "isDisplayedVesselDetail"]),
    scrollbarTag() {
      return this.$store.state.is_touch_device ? "div" : "VuePerfectScrollbar";
    },
    windowWidth() {
      return this.$store.state.windowWidth;
    },
    dragOptions() {
      return {
        animation: 200,
        disabled: !this.isEditOrder,
        ghostClass: "ghost"
      };
    },
    // ソート済み、フィルター済みのリストを返す
    displayFavoriteList() {
      return this.favoriteList.map(favorite => {
        return {
          label: favorite.label,
          groupList: favorite.groupList.map(group => {
            return {
              documentId: group.documentId,
              label: group.label,
              color: group.color,
              sort: group.sort,
              vesselList: this.sortVessels(group.vessels.filter(vesselId => this.isVesselIncludeKeyword(vesselId)))
            }
          }).sort((a, b) => {
            return a.sort - b.sort
          })
        }
      })
    },
  },
  async created() {
    let dataReady = await this.checkFirestoreDataReady()
    if (!dataReady) await this.createFirestoreData()
    await this.fetchFavoriteLabels()
    await this.fetchGroups()
    await this.subscribeGroups()
  },
  methods: {
    ...mapMutationsOfLiveMap([
      "SET_ACTIVE_SELECT_VESSEL_SIDEBAR",
    ]),
    ...mapActionsOfLiveMap([
      "jumpToMap",
      "showVesselDetail",
      "removeMonitoringVessel",
      "cleanUpVesselDetail",
      "fetchWake",
      "removeWake",
      "setSelectedVesselPosition",
      "checkFirestoreDataReady",
      "createFirestoreData",
      "subscribeGroups",
      "unsubscribeGroups",
      "getFavoriteLabels",
      "changeFavoriteLabel",
      "executeGroupAddSave",
      "executeGroupEditSave",
      "executeGroupEditDelete",
      "updateGroupSortOrder",
      "deleteVesselFromGroup",
      //"fetchMonitoringVessels",
      "fetchGroups"
    ]),

    getHoverColor(color) {
      const index = GROUP_COLOR_PALETTE.findIndex(element => {
        return element.normal == color
      })
      if (index != -1) {
        return GROUP_COLOR_PALETTE[index].deep
      }
    },

    sortVessels(vesselList) {
      const vesselListWithName = vesselList.map(vesselId => {
        const index = this.monitoringVessels.findIndex(vessel => {
          return vessel.id == vesselId
        })
        return {
          id: vesselId,
          name: this.monitoringVessels[index].name_ja ? this.monitoringVessels[index].name_ja :
            this.monitoringVessels[index].name ? this.monitoringVessels[index].name : ""
        }
      })
      const sortedVesselList = _.sortBy(vesselListWithName, "name").map(vessel => vessel.id)
      return sortedVesselList
    },

    fixFavoriteLabel() {
      if (!this.isFavoriteLabelComposing) {
        const labelInputElement = document.activeElement
        labelInputElement.blur();
      }
    },

    isVesselIncludeKeyword(vesselId) {
      const index = this.monitoringVessels.findIndex(element => element.id == vesselId)
      if (index != -1) {
        const vesselName = this.monitoringVessels[index].name_ja ? this.monitoringVessels[index].name_ja :
            this.monitoringVessels[index].name ? this.monitoringVessels[index].name : ""
        return vesselName.startsWith(this.searchKeyword)
      } else {
        return false
      }
    },

    getMonitoringVesselById(vesselId) {
      const index = this.monitoringVessels.findIndex(element => element.id == vesselId)
      return this.monitoringVessels[index]
    },

    async onRemoveMonitoringVessel(evt) {
      let removeVessel = await this.removeMonitoringVessel(evt);
      if (removeVessel) {
        let vesselName =
          removeVessel.name_ja ||
          removeVessel.name ||
          `${removeVessel.mmsi}(MMSI)`;
        this.$vs.loading.close();
        this.$vs.notify({
          title: "選択中の船舶を削除しました",
          text: `「${vesselName}」を選択中から削除しました`,
          color: "danger",
          position: "top-center"
        });

        if (this.isDisplayedVesselDetail(evt)) {
          await this.cleanUpVesselDetail(evt);
        }
      }
    },

    createExpiredPopup(emittedPopup) {
      if (this.expiredPopup !== null) {
        this.expiredPopup.remove();
        this.expiredPopup = null;
      }
      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }
      this.expiredPopup = emittedPopup
      setTimeout(() => {
        this.expiredPopup.addTo(this.map);
        this.expiredPopup.addClassName("expired-vessel-live-position-popup");
      }, 100);
      this.timerId = setTimeout(() => {
        this.expiredPopup.remove();
        this.expiredPopup = null;
      }, 60000); // expiredPopupは1分経過で自動で消える
    },

    async onFetchWake(evt) {
      if (!this.hasWakes(evt.vesselId)) {
        let payload = {
          from: 24,
          limit: 20000,
          vesselId: evt.vesselId
        };
        try {
          evt.cardComponent.isWakeLineLoading = true
          this.$vs.loading({
            color: "#2D9CDB",
            container: evt.cardComponent.$refs.wakeLineButton.$el,
            scale: 0.45
          })
          await this.fetchWake(payload);
          this.$vs.loading.close(evt.cardComponent.$refs.wakeLineButton.$el)
          evt.cardComponent.isWakeLineLoading = false
        } catch (e) {
          this.$vs.loading.close(evt.cardComponent.$refs.wakeLineButton.$el)
          evt.cardComponent.isWakeLineLoading = false
          this.$vs.notify({
            title: "航跡がありません",
            text: `「${evt.name}」の航跡がないため表示できません`,
            color: "warning",
            position: "top-center"
          });
        }
      } else {
        await this.removeWake(evt.vesselId);
      }
    },
    clickDeleteButton() {
      this.deletePopupActive = true
    },
    switchGroupEdit(groupDocumentId) {
      if (this.isEditOrder) return

      const index = this.openedEditGroups.findIndex(element => element == groupDocumentId)
      if (index == -1) {
        this.openedEditGroups.push(groupDocumentId)
      } else {
        this.openedEditGroups.splice(index, 1)
      }
    },
    switchGroupContent(groupDocumentId) {
      if (this.isEditOrder) return

      const index = this.openedContentGroups.findIndex(element => element == groupDocumentId)
      if (index == -1) {
        this.openedContentGroups.push(groupDocumentId)
      } else {
        this.openedContentGroups.splice(index, 1)
      }
    },
    switchGroupAddOpen() {
      this.isGroupAddOpen = this.isGroupAddOpen ? false : true
    },

    switchEditOrder() {
      this.openedContentGroups.splice(0)
      this.openedEditGroups.splice(0)
      if (this.isEditOrder) {
        this.isEditOrder = false
      } else {
        this.isEditOrder = true
      }
    },
    isTargetGroupContentOpened(groupDocumentId) {
      return this.openedContentGroups.includes(groupDocumentId)
    },
    isTargetGroupEditOpened(groupDocumentId) {
      return this.openedEditGroups.includes(groupDocumentId)
    },
    callUpdateGroupOrder(favoriteKey) {
      const sortList = this.displayFavoriteList[favoriteKey].groupList.map((group, index) => {
        return {
          documentId: group.documentId,
          sort: index
        }
      })
      this.updateGroupSortOrder({sortList: sortList, favoriteKey: favoriteKey})
    },
    callGroupAddSave(payload) {
      this.executeGroupAddSave({favoriteKey: this.selectedFavoriteIndex, groupInfo: payload})
      this.editGroupName = ""
      this.isGroupAddOpen = false
    },
    callGroupEditDelete(payload) {
      this.executeGroupEditDelete({favoriteKey: this.selectedFavoriteIndex, groupInfo: payload})
      this.openedEditGroups = this.openedEditGroups.filter(documentId => documentId != payload.documentId)
    },
    callGroupEditSave(payload) {
      this.executeGroupEditSave({favoriteKey: this.selectedFavoriteIndex, groupInfo: payload})
      this.openedEditGroups = this.openedEditGroups.filter(documentId => documentId != payload.documentId)
    },
    callDeleteVesselFromGroup(vesselId, favoriteKey, groupDocumentId) {
      this.deleteVesselFromGroup({vesselId: vesselId, favoriteKey: favoriteKey, groupDocumentId: groupDocumentId})
    },
    async fetchFavoriteLabels() {
      this.favoriteLabelList = await this.getFavoriteLabels()
    }
  },
  beforeDestroy() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    this.unsubscribeGroups();
  }
};
</script>

<style lang="scss">
@import "src/assets/scss/aisea/variables";

#map-controls-sidebar-select-vessel {
  .vs-sidebar {
    width: 400px;
    max-width: 90vw;
    background-color:  $aisea-dark-blue;
    opacity: 1.0;
  }

  .bookmark-area {
    height: 40px;
    display: flex;
    background: $aisea-blue-gray;
    .bookmark-button {
      display: flex;
      width: calc(100% / 3);
      input {
        width: 100%;
        text-align: center;
        display: block;
        align-items: center;
        justify-content: center;
        color: $aisea-pure-white;
        border: none;
        outline: none;
        background: none;
        opacity: 0.4;
        font-weight: bold;
        cursor: pointer;
      }
      &.selected {
        border-bottom: $aisea-blue solid 2px;
        input {
          opacity: 1;
          cursor: text;
        }
      }
    }
  }

  .search-control-area {
    display: flex;
    .search-area {
      flex: auto;
      position: relative;
      padding-left: 1rem;
      padding-right: 1rem;
      input {
        color: $aisea-pure-white;
        font-size: 15px;
        box-sizing: border-box;
        width: 100%;
        margin: 8px 0;
        padding: 0.5em;
        padding-left: 40px;
        transition: 0.3s;
        background: $aisea-blue-gray;
        border: 1px solid transparent;
        border-radius: 6px;
        outline: none;
        &::placeholder {
          color: #A0A3A7;
        }
        &:focus {
          box-sizing: border-box;
          border: 1px solid $aisea-blue;
        }
      }
      .search-icon {
        position: absolute;
        top: 7px;
        left: 1.3rem;
        padding: 9px 8px;
        transition: 0.3s;
        color: #A0A3A7;
      }
    }

    .control-area {
      display: flex;
      align-items: center;
      justify-content: end;
      .control-button {
        color: $aisea-pure-white;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 34px;
        height: 34px;
        margin-right: 14px;
        background: $aisea-blue-gray;
        border-radius: 4px;
        transition: 0.3s;
        cursor: pointer;
        &:hover {
          background: rgba(45, 156, 219, 0.6);
        }
        &.active {
          background: rgba(45, 156, 219, 1);
        }
      }

      .sort-switch {
        display: flex;
        color: $aisea-pure-white;
        background: $aisea-blue-gray;
        border-radius: 5px;
        margin-right: 14px;
        padding-right: 4px;
        align-items: center;
        .sort-icon {
          margin-left: 7px;
          margin-right: 7px;
        }
        .sort-button {
          width: 60px;
          text-align: center;
          border-radius: 5px;
          transition: 0.3s;
          margin-left: 2px;
          margin-right: 2px;
          cursor: pointer;
          &.selected {
            background: $aisea-blue !important;
          }
          &:hover {
            background: rgba(45, 156, 219, 0.6);
          }
        }
      }
    }
  }

  .scrollable-area {
    height: calc(100% - 170px);
    overflow-y: scroll;
    .group-container {
      margin-top: 5px;
      margin-bottom: 13px;
      .group-bar {
        position: relative;
        color: $aisea-pure-white;
        display: flex;
        background: $aisea-blue-gray;
        border-radius: 8px;
        border-left: solid 9px var(--color);
        box-sizing: border-box;
        padding: 8px 22px;
        margin: 8px 14px;
        font-size: 17px;
        cursor: pointer;
        .group-name {
          pointer-events: none;
          transition: 0.3s;
          z-index: 1;
        }
        &:hover, &.selected {
          //outline-offset: -1px;
          //outline: solid 1px var(--color);
          .group-name {
            color: var(--color);
          }
        }
        &.first {
          margin-top: 0px !important;
        }

        .content-length {
          pointer-events: none;
          line-height: 25px;
          min-width: 26px;
          margin-right: 10px;
          margin-left: auto;
          font-size: 14px;
          border-radius: 23px;
          text-align: center;
          display: inline-block;
          z-index: 1;
        }
        .more-icon {
          z-index: 1;
        }
        .group-bar-background {
          width: 100%;
          height: 100%;
          border-top-right-radius: 8px;
          border-bottom-right-radius: 8px;
          position: absolute;
          right: 0;
          top: 0;
          background: var(--hover);
          opacity: 0;
          transition: 0.3s;
          &:hover {
            opacity: 1;
          }
        }
      }
    }

    .group-edit-container {
      position: relative;
      height: 370px;
      color: $aisea-pure-white;
      background: $aisea-blue-gray;
      border-radius: 8px;
      padding: 16px 22px;
      margin: 8px 14px;
      font-size: 17px;
      .close-button{
        position: absolute;
        width: 23px;
        height: 23px;
        right: 20px;
      }
      .group-name-label {
        margin-top: 10px;
        font-size: 15px;
      }
      .group-name-input {
        position: relative;
        margin-top: 10px;
        margin-bottom: 30px;
        input {
          color: $aisea-pure-white;
          font-size: 15px;
          box-sizing: border-box;
          width: 100%;
          margin: 8px 0;
          padding: 0.5em;
          padding-left: 10px;
          transition: 0.3s;
          background: $aisea-blue-gray;
          border: 1px solid #C4C4C4;
          border-radius: 6px;
          outline: none;
          &::placeholder {
            color: #A0A3A7;
          }
          &:focus {
            box-sizing: border-box;
            border: 1px solid $aisea-blue;
          }
        }
      }
      .group-color-label {
        font-size: 15px;
      }
      .group-color-pallete {
        margin-top: 20px;
        margin-bottom: 20px;
        .color-pallete-line {
          display: flex;
          justify-content: space-between;
          margin-top: 10px;
          margin-bottom: 10px;
        }
        .color-chip {
          cursor: pointer;
        }
      }
      .control-spacer {
        height: 1px;
        margin-top: 30px;
        margin-bottom: 30px;
        background: #C4C4C4;
      }
      .button-area {
        display: flex;
        justify-content: space-between;
        button {
          appearance: none;
          color: $aisea-pure-white;
          width: 156px;
          height: 33px;
          display: flex;
          border-radius: 6px;
          justify-content: center;
          align-items: center;
          background: $aisea-blue-gray;
          transition: 0.3s;
          cursor: pointer;
        }
        .save-button{
          border: solid 1px $aisea-blue;
          &:hover {
            background: rgba(45, 156, 219, 0.4);
          }
        }
        .delete-button {
          border: solid 1px $aisea-red;
          &:hover {
            background: rgba(235, 87, 87, 0.4);
          }
        }
      }
    }
  }

  
  

  #selected-vessel-card {
    .vessel-img-area {
      position: relative;
    }

    .vessel-img-area p {
      position: absolute;
      top: 45%;
      left: 50%;
      color: $aisea-pure-white;
      -ms-transform: translate(-50%, -50%);
      -webkit-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);
      margin: 0;
      padding: 0;
    }

    .button-close {
      width: 23px !important;
      height: 23px !important;
      position: absolute;
      margin-right: -10px;
      top: -1px;
      right: 3px;
      z-index: 10;
    }

    img {
      width: 100%;
      object-fit: cover;
    }

    .vessel-ais-info-area {
      span {
        font-size: 13px;
      }
    }
  }

  .smooth-enter-active, .smooth-leave-active {
    transition: all 0.3s ease;
    overflow: hidden;
  }
  .smooth-enter, .smooth-leave-to {
    opacity: 0;
  }
}
</style>
